"use client";

import { useState } from "react";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: {
    phone: "Phone: ",
    email: "Email: ",
    address: "Address",
    load: "Load map",
    loaded: "Map loaded",
    hint: "Click “Load map” to display Google Maps.",
    map: "Map",
  },
  ro: {
    phone: "Telefon: ",
    email: "Email: ",
    address: "Adresă",
    load: "Încarcă harta",
    loaded: "Hartă încărcată",
    hint: "Apăsați „Încarcă harta” pentru a afișa Google Maps.",
    map: "Hartă",
  },
} as const;

type Props = {
  title: string;
  phone: string;
  phoneHref: string;
  email: string;
  addressLines: string[];
  mapSrc: string;
};

export default function ClickToLoadMap({
  title,
  phone,
  phoneHref,
  email,
  addressLines,
  mapSrc,
}: Props) {
  const t = copy[useLocale()];
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="rounded-2xl border border-[#27E8A7]/30 bg-black/80 backdrop-blur-sm shadow-[0_0_25px_rgba(39,232,167,0.10)] overflow-hidden">
      <div className="p-6 md:p-7">
        <h2 className="text-2xl md:text-3xl font-semibold text-white">{title}</h2>

        <div className="mt-4 space-y-2 text-sm text-white/75">
          <div>
            <span className="text-white/55">{t.phone}</span>
            <a className="text-[#27E8A7] hover:text-white" href={phoneHref}>
              {phone}
            </a>
          </div>
          <div>
            <span className="text-white/55">{t.email}</span>
            <a className="text-[#27E8A7] underline hover:text-white" href={`mailto:${email}`}>
              {email}
            </a>
          </div>

          <div className="pt-2">
            <div className="text-white/55 text-[11px] tracking-[0.16em] uppercase">{t.address}</div>
            <div className="mt-1 text-white/75">
              {addressLines.map((l) => (
                <div key={l}>{l}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5">
          {!loaded ? (
            <button
              type="button"
              onClick={() => setLoaded(true)}
              className="btn btn-outline"
            >
              {t.load}
            </button>
          ) : (
            <span className="inline-flex items-center rounded-full px-4 py-2 text-xs border border-[#27E8A7]/40 text-[#27E8A7]">
              {t.loaded}
            </span>
          )}
        </div>
      </div>

      {/* MAP */}
      <div className="relative bg-black">
        <div className="aspect-[16/10] md:aspect-[16/9]">
          {!loaded ? (
            <div className="w-full h-full bg-white/10 flex items-center justify-center">
              <div className="text-white/55 text-sm">
                {t.hint}
              </div>
            </div>
          ) : (
            <iframe
              key={mapSrc}
              src={mapSrc}
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              title={`${title} – ${t.map}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
